import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { announcementsApi, type ActiveAnnouncement } from '@/shared/api/announcements';
import { queryKeys } from '@/shared/api/queryKeys';

/**
 * Active announcements targeted at the signed-in user. Mounted in AppLayout, which
 * only renders after auth and has no /me bootstrap — so this query firing IS the
 * "on login" trigger.
 */
export function useActiveAnnouncements() {
  return useQuery({
    queryKey: queryKeys.announcements.active(),
    queryFn: () => announcementsApi.active(),
    staleTime: 5 * 60_000,
  });
}

/** Records that the viewer saw an announcement (best-effort, fire-and-forget). */
export function useMarkAnnouncementSeen() {
  return useMutation({
    mutationFn: (id: string) => announcementsApi.seen(id),
  });
}

/** Dismisses an announcement, optimistically removing it from the active list. */
export function useDismissAnnouncement() {
  const qc = useQueryClient();
  const key = queryKeys.announcements.active();

  return useMutation({
    mutationFn: (id: string) => announcementsApi.dismiss(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ActiveAnnouncement[]>(key);
      if (prev) {
        qc.setQueryData<ActiveAnnouncement[]>(key, prev.filter((a) => a._id !== id));
      }
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
  });
}
